import { calcInterests } from 'assets/js/methods'
import { collectAdIds } from '~/assets/js/adIds'
import route from 'vue-router'
import { AffiliateService } from '~/services/AffiliateService'
import Swal from 'sweetalert2'

const isPreviewMode = () => {
    if (!process.client || typeof window === 'undefined') {
        return false
    }

    return new URLSearchParams(window.location.search).get('preview') === 'true'
}

/** Aceita array, objeto indexado ou string JSON e devolve sempre um array. */
const normalizeToList = (raw) => {
    if (raw == null || raw === '') return []
    if (Array.isArray(raw)) return raw
    if (typeof raw === 'string') {
        try {
            return normalizeToList(JSON.parse(raw))
        } catch (_) {
            return []
        }
    }
    if (typeof raw === 'object') return Object.values(raw)

    return []
}

// O Nuxt liga o strict do Vuex fora de produção, e o OrderbumpsVariations altera
// variations/values do order bump (que vive no state) no beforeMount: em dev isso
// derruba a página com "do not mutate vuex store state outside mutation handlers".
// Em produção o strict já é falso, então aqui só alinhamos o dev ao comportamento real.
export const strict = false

export const state = () => ({
    company_domain: 'unicopag.com.br',
    company_name: '',
    store: '',
    gateway: '',
    gatewayUpsell: '',
    gateways: [],
    couponQueue: [],
    gateways_retry: [],
    product: '',
    cart: '',
    checkout: '',
    orderbumps: '',
    testimonials: '',
    custom_form_checkouts: [],
    custom_form_options: '',
    bonus: '',
    customer: '',
    pixels: '',
    paymentData: '',
    paymentMethodSelected: null,
    coupon: '',
    // Canonical list of applied coupons. `state.coupon` is kept in sync with
    // `state.coupons[0]` so legacy code that reads the single-coupon field
    // keeps working without changes.
    coupons: [],
    automatic_coupons: [],
    /** IDs/códigos de cupons de aplicação automática que o usuário removeu (não reaplicar na sessão) */
    removedAutomaticCouponIds: [],
    shippings: [],
    shipping: null,
    // Dados do afiliado
    affiliate: null,
    affiliatesConfig: null,
    affiliateHash: null,
    setup: {
        installments: 12,
        forceFreeShipping: false,
        isDev: process.env.NODE_ENV == 'development' ? true : false,
    },
    eventsFire: {
        InitiateCheckout: false,
        Lead: false,
        CompleteRegistration: false,
        AddPaymentInfo: false,
        Purchase: false,
    },
    tracking: {
        src: '',
        utm_source: '',
        utm_medium: '',
        utm_campaign: '',
        utm_term: '',
        utm_content: '',
        gclid: '',
        gbraid: '',
        wbraid: '',
    },
    custom_checkout: '',
    payment_methods: {
        creditcard: true,
        pix: true,
        billet: false,
    },
    payment_method_pre_selected: null,
    formState: {
        clickSubmitButton: false,
        inputSubmit: false,
        firstStepValid: false,
        addressValid: false,
        formValid: false,
    },
    transactionData: {},
    mercadopagoInstallments: '',
    appMaxInstallments: '',
    unicopagInstallments: '',
    tracker_init: false,
    klaviyoKey: '',
    device_id: null,
    datalayerModel: 'unicopag',
    pay_session_id: null,
    subscription: null,
    buyButtonText: 'Comprar agora',
})

export const getters = {
    getCoupon: (state) => (couponId) => {
        return state.couponQueue.find(coupon => coupon.id === couponId);
    },
    getCouponQueue(state) {
        return state.couponQueue;
    },
    getStore: (state) => {
        return state.store
    },
    getCustomCheckout: (state) => {
        return state.custom_checkout
    },
    getTestimonials: (state) => {
        return state.testimonials
    },
    getCustomFormCheckouts: (state) => {
        return state.custom_form_checkouts
    },
    getCustomFormOptions: (state) => {
        const raw = state.custom_form_options
        if (raw == null || raw === '') return {}
        if (typeof raw === 'object') return raw
        try {
            return JSON.parse(raw)
        } catch {
            return {}
        }
    },
    getLogisticsShippings: (state) => {
        return state.shippings
    },
    getProduct: (state) => {
        return state.product
    },
    getGateway: (state) => {
        return state.gateway
    },
    getGatewayUpsell: (state) => {
        return state.gatewayUpsell
    },
    getGateways: (state) => {
        return state.gateways
    },
    getCoupon: (state) => {
        return state.coupon
    },
    getCoupons: (state) => {
        // Prefer the canonical list. Fallback to the singular legacy field
        // wrapped in an array so call sites that opted into the new shape
        // still see manual coupons applied before this state was introduced.
        if (Array.isArray(state.coupons) && state.coupons.length > 0) {
            return state.coupons
        }
        return state.coupon ? [state.coupon] : []
    },
    getAutomaticCoupons: (state) => {
        return state.automatic_coupons
    },
    getCart: (state) => {
        return state.cart || JSON.parse(localStorage.getItem('cart'))
    },
    getCartOrderbumps: (state) => {
        let cartOrderbumps = state.cart.filter((u) => u.operation_type === 2)

        return cartOrderbumps
    },
    getCartWithoutOrderbumps: (state) => {
        let getCartWithoutOrderbumps = state.cart.filter(
            (u) => u.operation_type !== 2
        )

        return getCartWithoutOrderbumps
    },
    getPixels: (state) => {
        return state.pixels
    },
    getFormState: (state) => {
        return state.formState
    },
    isDev: (state) => {
        return state.setup['isDev'] ?? ''
    },
    firstStepValid: (state) => {
        return state.formState['firstStepValid'] ?? ''
    },
    getPaymentMethods: (state) => {
        return state.payment_methods
    },
    getPaymentMethodPreSelected: (state) => {
        return state.payment_method_pre_selected
    },
    getEventsState: (state) => {
        return state.eventsFire
    },
    getCustomer: (state) => {
        return state.customer
    },
    getOrderBumps: (state) => {
        return state.orderbumps
    },
    getBonus: (state) => {
        return state.bonus
    },
    getMercadoPagoInstallments: (state) => {
        return state.mercadopagoInstallments
    },
    getAppMaxInstallments: (state) => {
        return state.appMaxInstallments
    },
    getUnicopagInstallments: (state) => {
        return state.unicopagInstallments
    },
    getSumary: (state) => {
        let result = {},
            interest_amount = {},
            calc_installments = {},
            installments = 12,
            total_amount = 0,
            orderbump_total_amount = 0,
            upsell_total_amount = 0,
            subtotal = 0

        // Se for uma assinatura, usar os valores da assinatura
        if (state.subscription && state.subscription.planId) {
            result['total_amount'] = state.subscription.amountFirstCharge
            result['subscription_amount'] = state.subscription.amount
            result['subscription_amount_first_charge'] = state.subscription.amountFirstCharge
            result['subscription_periodicity'] = state.subscription.periodicity
            result['subscription_title'] = state.subscription.title
            result['is_subscription'] = true
            result['amount_discount'] = 0
            result['amount_discount_coupon'] = 0
            result['installments_data'] = []
            result['installments'] = 1
            result['free_installments'] = 1
            result['shipping'] = 0
            result['exists_shipping_paid'] = false
            result['subtotal'] = state.subscription.amountFirstCharge
            return result
        }

        let cart = state.cart || []

        if (cart) {
            let total_quantity = 0;
            cart.forEach((element) => {
                total_quantity += element.quantity
                total_amount += element.price * element.quantity
                subtotal += element.price * element.quantity

                if (element.operation_type === 2) {
                    orderbump_total_amount += element.price * element.quantity
                } else if (element.operation_type === 3) {
                    upsell_total_amount += element.price * element.quantity
                }
            })

            let discount_value = 0

            // Iterate over every applied coupon, accumulating the discount.
            // Each coupon's discount is computed against the REMAINING amount
            // (total_amount - discount_value) to mirror the server-side rule
            // applied by Coupon::sumDiscountByRules in the API.
            const appliedCoupons = Array.isArray(state.coupons) && state.coupons.length > 0
                ? state.coupons
                : (state.coupon && typeof state.coupon === 'object' ? [state.coupon] : [])

            appliedCoupons.forEach((coupon) => {
                if (!coupon || typeof coupon.discount === 'undefined') {
                    return
                }

                const remaining = Math.max(0, parseInt(total_amount) - discount_value)
                let coupon_discount = 0

                if (coupon?.rules?.includes('pay_for_two_get_three')) {
                    if (total_quantity >= 3) {
                        coupon_discount = cart.reduce((menor, item) => item.price < menor ? item.price : menor, cart[0].price)
                    }
                } else if (coupon?.rules?.includes('pay_for_one_get_three')) {
                    if (total_quantity >= 3) {
                        coupon_discount = cart.reduce((menor, item) => item.price < menor ? item.price : menor, cart[0].price) * 2
                    }
                } else if (coupon?.rules?.includes('pay_for_two_get_four')) {
                    if (total_quantity >= 4) {
                        coupon_discount = cart.reduce((menor, item) => item.price < menor ? item.price : menor, cart[0].price) * 2
                    }
                } else if (coupon?.rules?.includes('take_a_free_product')) {
                    coupon_discount = cart.reduce((menor, item) => item.price < menor ? item.price : menor, cart[0].price)
                } else if (coupon.discount_type === 'percent') {
                    coupon_discount = parseInt(
                        (parseInt(coupon.discount) / 100) * remaining
                    )
                } else if (coupon.discount_type === 'monetary') {
                    coupon_discount = parseInt(coupon.discount)
                }

                // Cap each coupon at remaining cart so oversized monetary
                // coupons never show negative totals / leftover credit.
                discount_value += Math.min(coupon_discount, remaining)
            })

            // Cap aggregate coupon discount at the cart total so multiple
            // monetary coupons cannot turn the order negative.
            discount_value = Math.min(discount_value, parseInt(total_amount))
            let discount_value_coupon = discount_value

            let discount_value_new = 0
            const igonre_orderbumps = state.store.discount_sum_with_orderbumps || !state.store.discount_sum_with_coupons;
            const ignore_upsells = state.store.discount_sum_with_upsells || !state.store.discount_sum_with_coupons;
            let total_discountable_amount = total_amount;
            total_discountable_amount -= (igonre_orderbumps ? orderbump_total_amount : 0);
            total_discountable_amount -= (ignore_upsells ? upsell_total_amount : 0)

            // Com acúmulo, o cupom entra primeiro e o % do método incide sobre o
            // que sobrou (R$ 799,60 - 50% = R$ 399,80; - 10% Pix = R$ 359,82), igual ao
            // EstimatePaymentController da API. Sem acúmulo, o método concorre com o
            // cupom pelo valor cheio.
            if (state.store.discount_sum_with_coupons !== 0) {
                total_discountable_amount = Math.max(0, total_discountable_amount - discount_value_coupon)
            }


            if (
                state.paymentMethodSelected === 1 &&
                state.store.discount_creditcard > 0
            ) {
                discount_value_new = parseInt(
                    (parseInt(state.store.discount_creditcard) / 100) *
                    parseInt(total_discountable_amount)
                )
            } else if (
                state.paymentMethodSelected === 2 &&
                state.store.discount_billet > 0
            ) {
                discount_value_new = parseInt(
                    (parseInt(state.store.discount_billet) / 100) *
                    parseInt(total_discountable_amount)
                )
            } else if (
                state.paymentMethodSelected === 3 &&
                state.store.discount_pix > 0
            ) {
                discount_value_new = parseInt(
                    (parseInt(state.store.discount_pix) / 100) *
                    parseInt(total_discountable_amount)
                )
            }

            if (state.store.discount_sum_with_coupons === 0) {
                if (discount_value_new > discount_value) {
                    discount_value = 0
                } else {
                    discount_value_new = 0
                }
            }

            discount_value = discount_value + discount_value_new

            // appliedDiscount = min(couponDiscount, cartSubtotal); total >= 0
            result['total_amount'] = Math.max(0, parseInt(total_amount) - discount_value)

            const exists_shipping_paid = state.shippings.filter((s) => s.price > 0).length > 0

            console.log('exists_shipping_paid:', exists_shipping_paid);

            let shipping = 0

            if (state.shipping && state.shipping.price > 0) {
                shipping = parseInt(state.shipping.price)
            }


            if (state.custom_checkout?.payment_method_pre_selected) {
                state.payment_method_pre_selected = state.custom_checkout?.payment_method_pre_selected
            }

            result.total_amount = Math.max(0, result.total_amount + shipping)

            let installments = state['setup']['installments']

            if (
                typeof state.gateway !== 'undefined' && typeof state.gateway.gateway_platform !== 'undefined' && state.gateway.gateway_platform === 'mercadopago' && state.paymentMethodSelected === 1
            ) {
                console.log('Enviar os dados de parcelamento do MercadoPago' + state.gateway.gateway_platform)

                if (state.mercadopagoInstallments !== '') {
                    var indice = 0

                    state.mercadopagoInstallments.forEach((v, k) => {
                        indice++
                        calc_installments[indice] = {
                            amount_input: result.total_amount,
                            // tax_interest: number,
                            amount_tax: v.installment_rate * 100,
                            amount_final: v.total_amount * 100,
                            amount_installment: v.installment_amount * 100,
                        }
                    })
                }
            } else if (typeof state.gateway !== 'undefined' && typeof state.gateway.gateway_platform !== 'undefined' && state.gateway.gateway_platform === 'appmax' && state.paymentMethodSelected === 1) {
                const custom_interest = state.gateway ? state.gateway.custom_interest : 0
                const free_installments = state.gateway ? state.gateway.free_installments : 1
                const hasAppMaxInstallments = state.appMaxInstallments !== ''
                    && state.appMaxInstallments !== null
                    && Object.keys(state.appMaxInstallments).length > 0

                if (hasAppMaxInstallments) {
                    var indice = 0

                    var arrayappMaxInstallments = Object.keys(
                        state.appMaxInstallments
                    ).map((i) =>
                        JSON.parse(state.appMaxInstallments[Number(i)])
                    )

                    arrayappMaxInstallments.forEach((v, k) => {
                        indice++

                        const appMaxTotal = v * 100
                        const isFreeInstallment = indice <= free_installments

                        calc_installments[indice] = {
                            amount_input: result.total_amount,
                            amount_final: isFreeInstallment ? result.total_amount : appMaxTotal,
                            amount_installment: isFreeInstallment
                                ? parseInt(result.total_amount / indice)
                                : parseInt(appMaxTotal / indice),
                            amount_tax: isFreeInstallment ? 0 : appMaxTotal - result.total_amount,
                        }
                    })
                } else {
                    for (var a = 1; a <= 12; a++) {
                        calc_installments[a] = calcInterests(
                            result.total_amount,
                            custom_interest ?? 0,
                            a,
                            free_installments
                        )
                    }
                }

                interest_amount = calcInterests(
                    result.total_amount,
                    custom_interest ?? 0,
                    installments,
                    free_installments
                )
            } else if (typeof state.gateway !== 'undefined' && typeof state.gateway.gateway_platform !== 'undefined' && state.gateway.gateway_platform === 'unicopag' && state.paymentMethodSelected === 1) {
                if (state.unicopagInstallments && Array.isArray(state.unicopagInstallments) && state.unicopagInstallments.length > 0) {
                    state.unicopagInstallments.forEach((v) => {
                        calc_installments[v.installments] = {
                            amount_input: result.total_amount,
                            amount_tax: (v.installment_rate || 0) * 100,
                            amount_final: v.total_amount,
                            amount_installment: v.installment_amount,
                        }
                    })
                }
            } else {

                var max_installments = 12;

                if (typeof state.gateway !== 'undefined' && state.gateway !== null && typeof state.gateway.recipient !== null) {
                    if (state.gateway.recipient && state.gateway.recipient.gateway === 'barte') {
                        var max_installments = 21;
                    }
                }

                const custom_interest = state.gateway ? state.gateway.custom_interest : 0;
                const free_installments = state.gateway ? state.gateway.free_installments : 0;
                // Tabela de juros por parcela do gateway; parcela fora dela usa o custom_interest
                const installment_rates = state.gateway ? state.gateway.installment_interest_rates : null;
                let interest_amount = calcInterests(
                    result['total_amount'],
                    custom_interest,
                    0,
                    installments,
                    free_installments,
                )

                for (var a = 1; a <= max_installments; a++) {
                    calc_installments[a] = calcInterests(
                        result.total_amount,
                        custom_interest ?? 0,
                        a,
                        free_installments,
                        installment_rates
                    )
                }
            }

            result['total_amount_installmented'] =
                interest_amount['amount_final'] ?? 0
            result['amount_installment'] =
                interest_amount['amount_installment'] ?? 0
            result['amount_discount'] = discount_value
            result['amount_discount_coupon'] = discount_value_coupon
            result['installments_data'] = calc_installments ?? []
            result['installments'] = installments ?? 12
            result['free_installments'] = state.gateway ? state.gateway.free_installments : 1
            result['shipping'] = state.product?.product?.tangible ? shipping : 0
            result['exists_shipping_paid'] = exists_shipping_paid
            result['subtotal'] = subtotal
        }

        return result
    },
    hasPhysicalProducts(state) {
        return state?.product?.product?.tangible
    },
    getInstallmentsData: (state) => {
        return state.installments_data
    },
    getDomain: (state) => {
        return state.company_domain;
    },
    getCompanyName: (state) => {
        return state.company_name;
    },
    getSetup: (state) => {
        return state.setup
    },
    getPlugins: (state) => {
        return state.plugins
    },
    getPaymentMethodSelected: (state) => {
        return state.paymentMethodSelected
    },
    getTracking: (state) => {
        return state.tracking
    },
    getPaymentData: (state) => {
        return state.paymentData
    },
    getInstallments: (state) => {
        return state.setup['installments']
    },
    getTransactionData: (state) => {
        return state.transactionData
    },
    getDatalayerModel: (state) => {
        return state.datalayerModel
    },
    getShipping: (state) => {
        return state.shipping
    },
    getTrackerInit: (state) => {
        return state.tracker_init
    },
    getKlaviyoKey: (state) => {
        return state.klaviyoKey
    },
    getDeviceId: (state) => {
        return state.device_id
    },
    getDfpId: (state) => {
        return state.dfp_id
    },
    getPaySessionId: (state) => {
        return state.pay_session_id
    },
    getSubscription: (state) => {
        return state.subscription
    },
    isSubscription: (state) => {
        return state.subscription && state.subscription.planId
    },
    getSubscriptionAmount: (state) => {
        return state.subscription?.amount || 0
    },
    getSubscriptionAmountFirstCharge: (state) => {
        return state.subscription?.amountFirstCharge || 0
    },
    getSubscriptionPeriodicity: (state) => {
        return state.subscription?.periodicity || 'monthly'
    },
    getSubscriptionTitle: (state) => {
        return state.subscription?.title || 'Assinatura'
    },
    getBuyButtonText: (state) => {
        return state.buyButtonText
    },
    // Getters de afiliado
    getAffiliate: (state) => {
        return state.affiliate
    },
    getAffiliatesConfig: (state) => {
        return state.affiliatesConfig
    },
    getAffiliateHash: (state) => {
        return state.affiliateHash
    },
    hasAffiliate: (state) => {
        return !!state.affiliateHash
    },
    getAffiliateData: (state) => {
        return {
            affiliate: state.affiliate,
            affiliatesConfig: state.affiliatesConfig,
            affiliateHash: state.affiliateHash,
        }
    },
}

export const mutations = {
    setCouponData(state, coupon) {
        state.coupon = coupon;
    },
    setCouponQueue(state, couponQueue) {
        state.couponQueue = couponQueue;
    },
    setForceFreeShipping(state, payload) {
        state.setup.forceFreeShipping = payload
    },
    setTrackerInit(state, payload) {
        state.tracker_init = payload
    },
    setStore(state, payload) {
        state.store = payload
    },
    setCustomCheckout(state, payload) {
        state.custom_checkout = payload
    },
    setCustomFormOptions(state, payload) {
        state.custom_form_options = JSON.stringify(payload)
    },
    setTestimonials(state, payload) {
        state.testimonials = payload
    },
    setCustomFormCheckouts(state, payload) {
        // A API monta essa coleção com `filter()->unique()` sem `values()`, o que
        // preserva as chaves originais — se o primeiro registro for descartado o
        // JSON chega como objeto indexado (`{"1": {...}}`) em vez de lista.
        // Normalizamos aqui pra que todos os layouts recebam sempre um array.
        state.custom_form_checkouts = normalizeToList(payload)
    },
    setLogisticsShippings(state, payload) {
        state.shippings = payload
    },
    setShippingSelected(state, payload) {
        state.shipping = payload
    },
    setProduct(state, payload) {
        state.product = payload
    },
    setCoupon(state, payload) {
        state.coupon = payload
        if (payload) {
            if (process.client) {
                if (typeof payload.rules == 'string') {
                    payload.rules = JSON.parse(payload.rules)
                }
                if (typeof payload.products == 'string') {
                    payload.products = JSON.parse(payload.products)
                }
                sessionStorage.setItem('coupon', JSON.stringify(payload))
            }
            state.coupons = [payload]
            if (process.client) {
                sessionStorage.setItem('coupons', JSON.stringify(state.coupons))
            }
        } else {
            state.coupons = []
            if (process.client) {
                sessionStorage.removeItem('coupons')
            }
        }
    },
    appendCoupon(state, payload) {
        if (!payload || typeof payload !== 'object') {
            return
        }

        if (process.client) {
            if (typeof payload.rules == 'string') {
                payload.rules = JSON.parse(payload.rules)
            }
            if (typeof payload.products == 'string') {
                payload.products = JSON.parse(payload.products)
            }
        }

        const list = Array.isArray(state.coupons) ? [...state.coupons] : []
        const exists = list.some((c) => {
            if (payload.id != null && c.id != null) return c.id === payload.id
            if (payload.code && c.code) return c.code === payload.code
            return false
        })

        if (!exists) {
            list.push(payload)
        }

        state.coupons = list
        state.coupon = list[0] || ''

        if (process.client) {
            sessionStorage.setItem('coupons', JSON.stringify(list))
            if (list[0]) {
                sessionStorage.setItem('coupon', JSON.stringify(list[0]))
            } else {
                sessionStorage.removeItem('coupon')
            }
        }
    },
    removeCouponById(state, identifier) {
        if (!Array.isArray(state.coupons) || state.coupons.length === 0) {
            return
        }

        const key = typeof identifier === 'object' && identifier !== null
            ? identifier
            : { id: identifier, code: identifier }

        const list = state.coupons.filter((c) => {
            if (key.id != null && c.id != null) return c.id !== key.id
            if (key.code && c.code) return c.code !== key.code
            return true
        })

        state.coupons = list
        state.coupon = list[0] || ''

        if (process.client) {
            if (list.length > 0) {
                sessionStorage.setItem('coupons', JSON.stringify(list))
                sessionStorage.setItem('coupon', JSON.stringify(list[0]))
            } else {
                sessionStorage.removeItem('coupons')
                sessionStorage.removeItem('coupon')
            }
        }
    },
    setAutomaticCoupons(state, payload) {
        state.automatic_coupons = payload
        if (payload) {
            if (process.client) {
                sessionStorage.setItem('automatic_coupons', JSON.stringify(payload))
                if (state.removedAutomaticCouponIds.length === 0) {
                    try {
                        const saved = sessionStorage.getItem('removedAutomaticCouponIds')
                        if (saved) state.removedAutomaticCouponIds = JSON.parse(saved)
                    } catch (e) {}
                }
            }
        }
    },
    setGateway(state, payload) {
        state.gateway = payload.find((u) => u.payment_method === 'all')
        state.gateways = payload
    },

    setGatewayUpsell(state, payload) {
        state.gatewayUpsell = payload
    },
    setGatewayToRetry(state, payload) {
        state.gateway = payload
    },
    setGatewaysRetry(state, payload) {
        state.gateways_retry = payload
    },
    setCartItem(state, payload) {
        let cartStorage = localStorage.getItem('cart') || JSON.stringify([])

        if (cartStorage) {
            cartStorage = JSON.parse(cartStorage)
        }

        if (cartStorage) {
            let checkCartExit = false

            // !TODO: Don't items duplicated from cart when store id Brota Company
            if (![1550, 1140].includes(cartStorage[0]?.store_id)) {
                checkCartExit = cartStorage.find(
                    (u) => u.cartvar === payload.cartvar
                )
            }

            if (!checkCartExit) {
                cartStorage.push(payload)
                state.cart = cartStorage

                if (process.client) {
                    localStorage.setItem('cart', JSON.stringify(cartStorage))
                }
            } else {
                let objIndex = cartStorage.findIndex(
                    (item) => item.cartvar === payload.cartvar
                )

                cartStorage[objIndex] = payload
                state.cart = cartStorage
            }

        }
    },
    /**
     * Substitui o carrinho no store (e localStorage) pelos itens retornados na transação,
     * no formato esperado por Events/getContents e init de pixels na página de cliente.
     */
    replaceCartWithTransactionItems(state, items) {
        if (!Array.isArray(items) || items.length === 0) {
            return
        }

        const storeId = state.store?.id ?? null
        const mapped = items.map((line, index) => {
            const productId =
                parseInt(String(line.product_id ?? line.productId ?? 0), 10) || 0
            const shopifyProductId =
                line.shopify_product_id != null
                    ? parseInt(String(line.shopify_product_id), 10)
                    : productId
            const shopifyVariationId =
                line.shopify_variation_id != null
                    ? parseInt(String(line.shopify_variation_id), 10)
                    : shopifyProductId
            const price =
                parseInt(String(line.price ?? line.sale_price ?? 0), 10) || 0
            const quantity =
                parseInt(String(line.quantity ?? 1), 10) || 1
            const title = line.title ?? line.product_name ?? ''
            const hashKey = line.variation_hash ?? line.hash ?? `p${productId}`
            const cartvar = `${hashKey}_${index}_txn`

            return {
                cartvar,
                id: line.id ?? shopifyVariationId ?? productId,
                store_id: line.store_id ?? storeId,
                hash: hashKey,
                variation_id: line.variation_id ?? shopifyVariationId,
                shopify_product_id: shopifyProductId,
                shopify_variation_id: shopifyVariationId,
                custom_options: line.custom_options,
                sku: line.sku ?? '',
                product_hash: line.product_hash ?? line.hash ?? '',
                product_id: productId,
                title,
                variation_title: line.variation_title ?? '',
                cover: line.variation_image ?? line.cover ?? '',
                price,
                quantity,
                operation_type: line.operation_type ?? 1,
            }
        })

        state.cart = mapped
        if (process.client) {
            localStorage.setItem('cart', JSON.stringify(mapped))
        }
    },
    updateQuantity(state, payload) {
        let cartStorage = JSON.parse(localStorage.getItem('cart')) || [];

        if (!cartStorage || !Array.isArray(cartStorage)) {
            cartStorage = [];
        }

        const cartIndex = cartStorage.findIndex(item => item.cartvar === payload.item.cartvar);
        if (cartIndex !== -1) {
            const cartItem = cartStorage[cartIndex];

            // Verificar stock_limit se existir
            //Criado por NathCessero
            const stockLimit = cartItem?.stock_limit;
            const hasStockLimit = stockLimit !== undefined && stockLimit !== null && stockLimit !== 0;

            if (payload.operation === 'plus') { //Criado por NathCessero
                // Verificar se atingiu o limite de estoque
                if (hasStockLimit && cartItem.quantity >= stockLimit) {
                    if (process.client) {
                        Swal.fire({
                            position: "top-end",
                            icon: "warning",
                            title: `Limite máximo atingido (${stockLimit})`,
                            text: `O item "${cartItem.name}" não pode ter mais de ${stockLimit} unidades.`,
                            showConfirmButton: false,
                            timer: 2500
                        });
                    }
                    return; // Não permite aumentar
                }
            //Fim da criação por NathCessero

                cartItem.quantity++;
            } else if (payload.operation === 'less' && cartItem.quantity > 1) {
                cartItem.quantity--;
            } else {
                if (this.getters['getCartWithoutOrderbumps'].length > 1) {
                    cartStorage.splice(cartIndex, 1);
                }
            }

            // Atribui uma nova referência ao objeto para o Vue detectar a mudança
            state.cart = [...cartStorage];

            if (process.client) {
                localStorage.setItem('cart', JSON.stringify(cartStorage));
            }

            // Calcular o total do carrinho
            const totalValue = cartStorage.reduce((total, item) => total + ((item.price / 100) * item.quantity), 0);

            // Verifica regras de cupom baseadas na quantidade
            const totalQuantity = cartStorage.reduce((total, item) => total + item.quantity, 0);
            const minItemsInCart = this.getters['getCoupon'].min_items_in_cart;
            const minValueInCart = this.getters['getCoupon'].min_value_in_cart;

            if (totalQuantity < minItemsInCart) {
                state.coupon = '';
                localStorage.removeItem('coupon');
            }

            if (totalValue < minValueInCart) {
                state.coupon = '';
                localStorage.removeItem('coupon');
            }

            const coupons = this.getters['getAutomaticCoupons'];
            const removedIds = state.removedAutomaticCouponIds || [];

            if (coupons.length > 0) {
                const cartTotalValue = cartStorage.reduce((total, item) => total + ((item.price / 100) * item.quantity), 0);
                const cartTotalQuantity = cartStorage.reduce((total, item) => total + (item.quantity), 0);
                const eligibleCoupons = coupons.filter(
                    (c) => !removedIds.includes(c.id != null ? c.id : c.code)
                );
                // When allow_oversized_coupon_discount is on, monetary coupons
                // may exceed the cart (order becomes free). Otherwise keep the
                // legacy filter that rejects oversized auto-apply candidates.
                const allowOversized = Boolean(
                    state.custom_checkout?.allow_oversized_coupon_discount
                )
                let coupon_applyed = eligibleCoupons.find((coupon) => {
                    if (coupon.min_items_in_cart > cartTotalQuantity) return false
                    if (coupon.min_value_in_cart >= cartTotalValue) return false
                    if (
                        !allowOversized &&
                        coupon.discount_formatted >= cartTotalValue
                    ) {
                        return false
                    }
                    return true
                });

                if (coupon_applyed) {
                    coupon_applyed = JSON.parse(JSON.stringify(coupon_applyed));
                    this.commit('appendCoupon', coupon_applyed);
                }
            }
        }
    },
    updateCustomOptionQuantity(state, payload) {
        let cartStorage = JSON.parse(localStorage.getItem('cart')) || [];

        if (!cartStorage || !Array.isArray(cartStorage)) {
            cartStorage = [];
        }

        const cartIndex = cartStorage.findIndex(item => item.cartvar === payload.item.cartvar);
        if (cartIndex !== -1) {
            const cartItem = cartStorage[cartIndex];

            if (payload.operation === 'plus') {
                cartItem.custom_options[payload.optionIndex].quantity++;
            } else if (payload.operation === 'less' && cartItem.custom_options[payload.optionIndex].quantity > 1) {
                cartItem.custom_options[payload.optionIndex].quantity--;
            } else {
                cartItem.custom_options.splice(payload.optionIndex, 1);
            }

            state.cart = cartStorage;
            if (process.client) {
                localStorage.setItem('cart', JSON.stringify(cartStorage));
            }

            this.commit('updateQuantity', { item: cartItem, operation: payload.operation });

        }
    },
    removeCartItem(state, payload) {
        console.log(payload)
        let cart = state.cart
        cart = cart.filter((item) => item.cartvar !== payload.cartvar)
        state.cart = cart

        if (process.client) {
            localStorage.setItem('cart', JSON.stringify(cart))
        }
    },
    removeCoupon(state) {
        const automatic = state.automatic_coupons || []
        const appliedList = Array.isArray(state.coupons) && state.coupons.length > 0
            ? state.coupons
            : (state.coupon ? [state.coupon] : [])

        appliedList.forEach((current) => {
            if (!current || !automatic.length) return
            const isAutomatic = automatic.some(
                (ac) => (ac.id != null && ac.id === current.id) || ac.code === current.code
            )
            if (!isAutomatic) return

            const idToBlock = current.id != null ? current.id : current.code
            if (!state.removedAutomaticCouponIds.includes(idToBlock)) {
                state.removedAutomaticCouponIds.push(idToBlock)
                if (process.client) {
                    sessionStorage.setItem('removedAutomaticCouponIds', JSON.stringify(state.removedAutomaticCouponIds))
                }
            }
        })

        state.coupon = ''
        state.coupons = []
        localStorage.removeItem('coupon')
        if (process.client) {
            sessionStorage.removeItem('coupons')
        }
    },
    setOrderBumps(state, payload) {
        state['orderbumps'] = payload
    },
    setFirstStepValid(state, payload) {
        state['formState']['firstStepValid'] = payload
    },
    setPaymentMethodSelected(state, payload) {
        if (payload) {
            let gateway_default = state.gateways.find(
                (u) => u.payment_method === 'all'
            )

            state.gateway = gateway_default

            state.gateways.forEach((v, k) => {
                if (v.payment_method === 'creditcard' && payload === 1) {
                    state.gateway = v
                }

                if (v.payment_method === 'billet' && payload === 2) {
                    state.gateway = v
                }

                if (v.payment_method === 'pix' && payload === 3) {
                    state.gateway = v
                }

                state['paymentMethodSelected'] = payload
            })
        }
    },
    setPaymentMethods(state, payload) {
        if (payload) {
            state['payment_methods'] = payload
        }
    },
    setPaymentMethodPreSelected(state, payload) {
        state['payment_method_pre_selected'] = payload
    },
    setEventsState(state, payload) {
        payload['InitiateCheckout'] ?
            (state['eventsFire']['InitiateCheckout'] =
                payload['InitiateCheckout']) :
            undefined
        payload['Lead'] ?
            (state['eventsFire']['Lead'] = payload['Lead']) :
            undefined
        payload['AddPaymentInfo'] ?
            (state['eventsFire']['AddPaymentInfo'] =
                payload['AddPaymentInfo']) :
            undefined
        payload['Purchase'] ?
            (state['eventsFire']['Purchase'] = payload['Purchase']) :
            undefined
    },
    setPurchaseEventFlag(state, value) {
        state.eventsFire.Purchase = Boolean(value)
    },
    setPixels(state, payload) {
        if (isPreviewMode()) {
            state['pixels'] = ''
            return
        }

        state['pixels'] = payload
    },
    setTracking(state, payload) {
        state['tracking'] = payload
    },
    setBonus(state, payload) {
        if (payload) {
            state['bonus'] = payload
        }
    },
    setCustomer(state, payload) {
        if (typeof payload['cpf'] !== 'undefined') {
            payload['document'] = payload['cpf']
        }

        if (payload.email) {
            payload['email'] = payload.email
                .normalize('NFD')
                .replace(/\p{Mn}/gu, '')
                .toLocaleLowerCase()
        }

        state['customer'] = JSON.parse(JSON.stringify(payload))
        if (payload) {
            localStorage.setItem('customer', JSON.stringify(payload))
        }
    },
    setPaymentData(state, payload) {
        state['paymentData'] = JSON.parse(JSON.stringify(payload))
    },
    setInstallments(state, payload) {
        state['setup']['installments'] = parseInt(payload)
    },
    setTransactionData(state, payload) {
        state['transactionData'] = payload
    },
    setDatalayerModel(state, payload) {
        state['datalayerModel'] = payload
    },
    setMercadoPagoInstallments(state, payload) {
        state.mercadopagoInstallments = payload
    },
    setAppMaxInstallments(state, payload) {
        state.appMaxInstallments = payload
    },
    setUnicopagInstallments(state, payload) {
        state.unicopagInstallments = payload
    },
    setCompanyName(state, payload) {
        state.company_name = payload
    },
    setCompanyDomain(state, payload) {
        state.company_domain = payload
    },
    setKlaviyoKey(state, payload) {
        state.klaviyoKey = payload
    },
    setDeviceId(state, payload) {
        state.device_id = payload
    },
    setDfpId(state, payload) {
        state.dfp_id = payload
    },
    setPaySessionId(state, payload) {
        state.pay_session_id = payload
    },
    clearShipping(state) {
        state.shipping = null
    },
    setSubscription(state, payload) {
        state.subscription = payload
    },
    setBuyButtonText(state, payload) {
        state.buyButtonText = payload
    },
    // Mutations de afiliado
    setAffiliate(state, payload) {
        state.affiliate = payload
    },
    setAffiliatesConfig(state, payload) {
        state.affiliatesConfig = payload
    },
    setAffiliateHash(state, payload) {
        state.affiliateHash = payload
    },
    /**
     * Processa dados de afiliado vindos da API
     * @param {Object} state - Estado do Vuex
     * @param {Object} payload - { data: responseData, route: $route }
     */
    processAffiliateData(state, payload) {
        const { data, route } = payload

        // Define os dados do afiliado no state
        state.affiliate = data.affiliate || null
        state.affiliatesConfig = data.affiliates_config || null

        // Processa o hash do afiliado usando o AffiliateService
        if (process.client) {
            const affiliateHash = AffiliateService.processAffiliateConfiguration(data, route)
            state.affiliateHash = affiliateHash
        }
    },
    clearAffiliateData(state) {
        state.affiliate = null
        state.affiliatesConfig = null
        state.affiliateHash = null

        if (process.client) {
            AffiliateService.clearAffiliateData()
        }
    },
}

export const actions = {
    setCustomer({ commit }, payload) {
        let dataLead = payload
        if (process.client) {
            if (localStorage.getItem('customer')) {
                commit(
                    'setCustomer',
                    JSON.parse(localStorage.getItem('customer'))
                )
                dataLead = JSON.parse(localStorage.getItem('customer'))
            } else {
                commit('setCustomer', JSON.parse(JSON.stringify(payload)))
            }
        } else {
            commit('setCustomer', JSON.parse(JSON.stringify(payload)))
        }
        return dataLead
    },
    setCartItem({ commit }, payload) {
        let dataCart = payload
        if (process.client) {
            if (localStorage.getItem('cart')) {
                commit('setCartItem', JSON.parse(localStorage.getItem('cart')))
                dataCart = JSON.parse(localStorage.getItem('cart'))
            } else {
                commit('setCartItem', JSON.parse(JSON.stringify(payload)))
            }
        } else {
            commit('setCartItem', JSON.parse(JSON.stringify(payload)))
        }
        return dataCart
    },
    pushErros({ commit }, payload) {
        fetch(`https://api.unicopag.com.br/api/api/notifications/errors`, {
            method: 'POST',
            mode: 'cors',
            headers: {
                Accept: 'application/json',
                'Content-Type': 'application/json',
            },
            body: JSON.stringify({
                title: payload.title,
                message: payload.message,
                sound: payload.sound ?? 'pushover',
            }),
        })
    },
    callEventTracker({ commit }, payload) {
        setTimeout(() => {
            try {
                if (isPreviewMode()) {
                    return
                }

                if (this.state.tracker_init === true) {
                    // Disparar o evento via commit para o Events component
                    commit('setEventsState', {
                        [payload.event]: true
                    })
                    // _ga/_ga_<ID>, _ttp, ttclid, gclid... vão junto para a API usar nos eventos
                    // server-side (GA4 Measurement Protocol, TikTok Events API).
                    const extraParams = { ...(payload.extra_params || {}), ad_ids: collectAdIds() }
                    callTrackEvent(payload.event, extraParams, this.state.company_domain)
                }
            } catch (error) {
                console.error(error)
            }
        }, 1000)
    },
    setSubscription({ commit }, payload) {
        commit('setSubscription', payload)
    },
    setBuyButtonText({ commit }, payload) {
        commit('setBuyButtonText', payload)
    },
}
